/**
 * When Backoffice should keep watching a company's materials, and why.
 *
 * Kept pure and free of Vue and Apollo so the rules can be reasoned about and
 * tested on their own. The component decides *how* to poll; this decides
 * *whether* to, which is where every previous bug lived:
 *
 * - Observation used to start only once rows already existed, so the minutes
 *   right after activation — exactly when a user is watching — showed nothing.
 * - `PENDING` was not counted as work in progress, so a freshly created
 *   material looked finished.
 * - Nothing ever stopped it, so an idle company polled forever.
 */

/** Statuses that mean a material is still being worked on. */
export const NON_TERMINAL_MATERIAL_STATUSES = Object.freeze([
  // `PENDING` is the initial state a material is created in. Omitting it is why
  // observation dead-started on a company whose rows had just been provisioned.
  'PENDING',
  'QUEUED',
  'GENERATING'
])

/** How long to keep watching a company that never reaches a terminal state. */
export const MAX_OBSERVATION_MS = 10 * 60 * 1000

/**
 * How long an ACTIVE company's empty landing page is assumed to be provisioning.
 *
 * Bounded on purpose: a landing page that simply has no materials is
 * indistinguishable from one whose rows have not been created yet, so the
 * "still provisioning" reading has to expire rather than poll forever.
 */
export const PROVISIONING_GRACE_MS = 2 * 60 * 1000

/** Whether one material is still being worked on. */
export function isNonTerminal (material) {
  return NON_TERMINAL_MATERIAL_STATUSES.includes(material?.status)
}

/** Whether a company is in the state that provisions and generates materials. */
export function isActiveCompany (company) {
  const stringValues = company?.state?.stringValues || []
  return stringValues[stringValues.length - 1] === 'ACTIVE' || company?.status === 'ACTIVE'
}

/**
 * Why observation is still running, or `null` when it is not.
 *
 * - `PROVISIONING` — the company is ACTIVE but its material rows do not exist
 *   yet. This is the window the old implementation could not represent at all,
 *   and the reason the screen looked empty and idle right after activation.
 * - `GENERATING` — rows exist and at least one is still being worked on.
 *
 * @param company The company, for its lifecycle state.
 * @param materials Every material currently known for the company.
 * @param landingpages The company's landing pages, used to notice rows that
 *   have not been created yet.
 * @param elapsedMs How long observation has been running, for the bounded
 *   fallbacks. Passed in rather than read from a clock so the rule stays pure.
 */
export function observationReason ({ company, materials, landingpages, elapsedMs }) {
  if (elapsedMs >= MAX_OBSERVATION_MS) return null
  if (!isActiveCompany(company)) return null

  const rows = materials || []
  if (rows.some(isNonTerminal)) return 'GENERATING'

  // Within the grace window, an ACTIVE company with no rows at all — or with a
  // landing page that has none — is read as still being provisioned.
  if (elapsedMs < PROVISIONING_GRACE_MS) {
    if (rows.length === 0) return 'PROVISIONING'
    const withMaterials = new Set(rows.map(({ landingpageId }) => landingpageId).filter(Boolean))
    const awaiting = (landingpages || []).some(({ id }) => !withMaterials.has(id))
    if (awaiting) return 'PROVISIONING'
  }

  return null
}

/** Whether observation should keep polling. */
export function shouldObserve (input) {
  return observationReason(input) !== null
}

/**
 * Whether one landing page's materials are still being provisioned.
 *
 * Drives the per-landing-page placeholder, so a user sees "being prepared"
 * instead of an empty row that reads as "nothing will ever appear here".
 */
export function isLandingpageProvisioning ({ company, landingpageMaterials, elapsedMs }) {
  if (!isActiveCompany(company)) return false
  if ((landingpageMaterials || []).length === 0) {
    return elapsedMs < PROVISIONING_GRACE_MS
  }
  return landingpageMaterials.some(isNonTerminal)
}
